import { maybe, Maybe, rd, RemoteData } from '@passionware/monads';
import { ensureError } from '@passionware/platform-js';
import { AnimatePresence, motion } from 'motion/react';
import { ReactNode, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { UseFormReturn } from 'react-hook-form';
import { CustomFieldType } from '../../../../__types__';
import { Checkbox } from '../../../design-system/Checkbox';
import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage
} from '../../../design-system/Form';
import { Input } from '../../../design-system/Input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '../../../design-system/Select';
import { Separator } from '../../../design-system/Separator';
import { AtellioFeatureGroupDefinition } from '../../../features';
import { InfoCallout } from 'v5/patterns/feedback/InfoCallout';
import { FormDialogError } from 'v5/patterns/forms/dialogs/FormDialogError';
import { FeatureFlagValues } from '../../../platform/feature-management/feature-management';
import {
  buildCustomFieldNameFieldRules,
  FieldDefinition,
  FieldFormModel
} from './field-definition';
import { getFields } from './fields';
import { MeasurementFieldConfig } from './fields/MeasurementFieldConfig';
import { SingleSelectFieldConfig } from './fields/SingleSelectFieldConfig';
import { CreateFieldSkeleton } from './skeleton';
import { useExistingCustomFieldNames } from './useExistingCustomFieldNames';

const extraConfig = {
  [CustomFieldType.MEASUREMENT_NEW]: {
    key: 'measurement',
    Component: MeasurementFieldConfig
  },
  [CustomFieldType.SINGLE_SELECT]: {
    key: 'select',
    Component: SingleSelectFieldConfig
  },
  [CustomFieldType.MULTI_SELECT]: {
    key: 'select',
    Component: SingleSelectFieldConfig
  },
  [CustomFieldType.NETSUITE_SUBCLASS]: {
    key: 'select',
    Component: SingleSelectFieldConfig
  }
};

export type CustomFieldConfigFieldList = ReturnType<typeof getFields>;

export interface CustomFieldConfigFormFieldsProps {
  form: UseFormReturn<FieldFormModel>;
  initialDefinition: Maybe<FieldDefinition>;
  fields: CustomFieldConfigFieldList;
  features: Partial<FeatureFlagValues<AtellioFeatureGroupDefinition>>;
  disabledFields?: (keyof FieldFormModel)[];
  hideDisabledFieldTypeOptions?: boolean;
  autoFocusName?: boolean;
}

/**
 * Custom field configuration form (name, type, type-specific config, flags).
 * Expects a parent `Form`. `initialDefinition` is `null` when creating a field, or the
 * existing definition when editing (used e.g. to keep the current type visible in the select).
 */
export function CustomFieldConfigFormFields({
  form,
  initialDefinition,
  fields,
  features,
  disabledFields,
  hideDisabledFieldTypeOptions,
  autoFocusName
}: CustomFieldConfigFormFieldsProps) {
  const { t } = useTranslation('patterns');
  const existingFieldNames = useExistingCustomFieldNames();

  const initialDefinitionName = maybe.isPresent(initialDefinition)
    ? initialDefinition.name.trim()
    : null;

  const nameFieldRules = useMemo(
    () =>
      buildCustomFieldNameFieldRules(
        {
          required: t('CustomFieldAddFlow.nameValidation', {
            context: 'required'
          }),
          minLength: t('CustomFieldAddFlow.nameValidation', {
            context: 'minLength'
          }),
          duplicate: t('CustomFieldAddFlow.nameValidation', {
            context: 'duplicate'
          })
        },
        existingFieldNames,
        initialDefinitionName
      ),
    [existingFieldNames, initialDefinitionName, t]
  );

  return (
    <>
      <FormField
        control={form.control}
        name="name"
        rules={nameFieldRules}
        render={({ field }) => (
          <FormItem>
            <FormLabel>Name</FormLabel>
            <FormControl>
              <Input placeholder="Name" autoFocus={autoFocusName} {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
      <Separator />
      <FormField
        control={form.control}
        name="type"
        rules={{
          required: 'Field type is required'
        }}
        render={({ field }) => (
          <FormItem>
            <FormLabel>Field type</FormLabel>
            <Select
              onValueChange={field.onChange}
              defaultValue={maybe.getOrUndefined(field.value)}
              disabled={disabledFields?.includes('type')}
            >
              <FormControl>
                <SelectTrigger>
                  <SelectValue placeholder="Select a field type"></SelectValue>
                </SelectTrigger>
              </FormControl>
              <SelectContent>
                {fields
                  .filter(field => {
                    if (initialDefinition?.type === field.type) {
                      return true;
                    }
                    const isAllowedByFeatureFlag =
                      !features ||
                      !field.featureFlag ||
                      features[field.featureFlag];
                    if (!isAllowedByFeatureFlag) {
                      return false;
                    }
                    const shouldBeHiddenAsDisabled =
                      hideDisabledFieldTypeOptions && field.disabled;

                    return !shouldBeHiddenAsDisabled;
                  })
                  .map(field => {
                    return (
                      <SelectItem
                        key={field.type}
                        value={field.type}
                        className="max-w-(--radix-select-trigger-width)"
                        disabled={field.disabled}
                      >
                        <img
                          className="m-2"
                          src={field.icon}
                          alt={field.label}
                        />
                        <div>
                          <div>{field.label}</div>
                          <div className="text-fg-muted group-[.is-trigger]:hidden pt-1">
                            {field.description}
                          </div>
                        </div>
                      </SelectItem>
                    );
                  })}
              </SelectContent>
            </Select>
            <FormMessage />
          </FormItem>
        )}
      />
      <AnimatePresence presenceAffectsLayout mode="wait">
        {maybe.map(form.watch('type'), type => {
          const config = extraConfig[type];
          if (!config) return null;
          return (
            <motion.div
              className="space-y-6"
              key={config.key}
              initial={{
                opacity: 0,
                height: 0,
                overflow: 'hidden'
              }}
              animate={{
                opacity: 1,
                height: 'auto',
                overflow: 'visible'
              }}
              exit={{
                opacity: 0,
                height: 0,
                overflow: 'hidden',
                transition: { ease: 'circIn', duration: 0.15 }
              }}
            >
              <config.Component form={form} />
            </motion.div>
          );
        })}
      </AnimatePresence>
      <Separator />
      <FormField
        control={form.control}
        name="isRequired"
        render={({ field }) => (
          <FormItem className="flex flex-row items-start space-x-3 space-y-0 rounded-md">
            <FormControl>
              <Checkbox
                checked={field.value}
                onCheckedChange={field.onChange}
              />
            </FormControl>
            <div className="space-y-1 leading-none">
              <FormLabel>Required field</FormLabel>
              <FormDescription>
                Entities with this field will show an error if it is empty
              </FormDescription>
            </div>
          </FormItem>
        )}
      />
      <FormField
        control={form.control}
        name="isFilterable"
        render={({ field }) => (
          <FormItem className="flex flex-row items-start space-x-3 space-y-0 rounded-md">
            <FormControl>
              <Checkbox
                checked={field.value}
                onCheckedChange={field.onChange}
              />
            </FormControl>
            <div className="space-y-1">
              <FormLabel>Filterable</FormLabel>
              <FormDescription>
                Users can search against this field
              </FormDescription>
            </div>
          </FormItem>
        )}
      />
    </>
  );
}

CustomFieldConfigFormFields.displayName = 'CustomFieldConfigFormFields';

export type CustomFieldConfigFormContentProps = Omit<
  CustomFieldConfigFormFieldsProps,
  'initialDefinition'
> & {
  /** Resolved initial field from store/API (`RemoteData` drives skeleton vs form). */
  initialField: RemoteData<Maybe<FieldDefinition>>;
  prompt: ReactNode;
  autoFocusName?: boolean;
};

/**
 * Loading / error handling for initial field data, then {@link CustomFieldConfigFormFields},
 * plus optional prompt — usable inside any form layout (dialog, drawer, page).
 */
export function CustomFieldConfigFormContent({
  initialField,
  form,
  fields,
  features,
  disabledFields,
  hideDisabledFieldTypeOptions,
  prompt,
  autoFocusName
}: CustomFieldConfigFormContentProps) {
  return (
    <>
      {rd
        .journey(initialField)
        .wait(<CreateFieldSkeleton />)
        .catch(e => (
          <FormDialogError
            error={ensureError(e)}
            title="Unable to Load Field Configuration"
            description="Something went wrong while loading the field configuration. Please try again."
            onRetry={() => window.location.reload()}
          />
        ))
        .map(definition => (
          <CustomFieldConfigFormFields
            form={form}
            initialDefinition={definition}
            fields={fields}
            features={features}
            disabledFields={disabledFields}
            hideDisabledFieldTypeOptions={hideDisabledFieldTypeOptions}
            autoFocusName={autoFocusName}
          />
        ))}
      {prompt && <InfoCallout className="mt-8">{prompt}</InfoCallout>}
    </>
  );
}

CustomFieldConfigFormContent.displayName = 'CustomFieldConfigFormContent';
